<template>
  <div id="user">
    <transition name="slide-fade" mode="out-in">
      <div class="mcontainer" v-if="$route.name === 'AllUser'">
        <!-- Header -->
        <div class="header-bar">
          <h2>All User</h2>

          <div class="actions">
            <div class="search">
              <Icon icon="mdi:magnify" width="18" height="18" class="search-icon" />
              <input
                type="text"
                class="search-input"
                v-model="search"
                @input="onSearchInput"
                placeholder="Search users..."
                aria-label="Search users"
              />
            </div>

            <button class="btn btn-outline" @click="openFilterModal" aria-haspopup="dialog" aria-controls="filterModal">
              <Icon icon="mdi:tune-variant" width="18" height="18" />
              <span>Filters</span>
            </button>

            <!-- Import -->
            <button class="btn btn-primary" @click="openImportModal" aria-haspopup="dialog" aria-controls="importModal">
              <Icon icon="mdi:tray-arrow-up" width="18" height="18" />
              <span>Import</span>
            </button>
          </div>
        </div>

        <!-- Card -->
        <div class="card">
          <!-- Table -->
          <div class="table-wrapper">
            <table class="table">
              <thead>
                <tr>
                  <th>Fullname</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Faculty</th>
                  <th>No Induk</th>
                  <th>Status</th>
                  <th class="w-150">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="data in all_user" :key="data.id">
                  <td>{{ data.fullname }}</td>
                  <td class="muted">{{ data.email }}</td>
                  <td>{{ data.type }}</td>
                  <td>{{ data.faculity }}</td>
                  <td>{{ data.no_induk }}</td>
                  <td>
                    <span class="badge" :class="data.is_banned == 0 ? 'badge-success' : 'badge-danger'">
                      {{ data.is_banned == 0 ? 'Active' : 'Banned' }}
                    </span>
                  </td>
                  <td>
                    <div class="row-actions">
                      <button
                        v-if="data.is_banned == 0"
                        class="btn btn-danger"
                        @click="banned(data.id, 'Banned')"
                        title="Banned"
                      >
                        <Icon icon="ci:off-outline-close" />
                      </button>
                      <button
                        v-else
                        class="btn btn-warning"
                        @click="banned(data.id, 'Unbanned')"
                        title="Unbanned"
                      >
                        <Icon icon="icon-park-outline:refresh-one" />
                      </button>
                      <router-link
                        :to="'/app/all_user/update/' + data.username"
                        class="btn btn-primary"
                        title="Edit"
                      >
                        <Icon icon="akar-icons:edit" />
                      </router-link>
                    </div>
                  </td>
                </tr>

                <tr v-if="!loading && all_user.length === 0">
                  <td colspan="7" class="empty-state">
                    <Icon icon="mdi:account-search-outline" width="22" height="22" />
                    <span>No data found</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="loading-wrap" v-if="loading">
            <b-spinner label="Loading..." small />
          </div>

          <!-- Pagination -->
          <div v-if="totalPage > 1" class="pager-wrap">
            <div class="pager-info">Page {{ page }} of {{ totalPage }}</div>

            <div class="pager-controls">
              <button
                class="icon-btn"
                :disabled="page === 1 || loading"
                @click="goToPage(1)"
                aria-label="First page"
                title="First"
              >
                <Icon icon="mdi:chevron-double-left" width="18" height="18" />
              </button>

              <button
                class="icon-btn"
                :disabled="page === 1 || loading"
                @click="goToPage(page - 1)"
                aria-label="Previous page"
                title="Prev"
              >
                <Icon icon="mdi:chevron-left" width="18" height="18" />
              </button>

              <span v-for="token in pageTokens" :key="tokenKey(token)">
                <button
                  v-if="typeof token === 'number'"
                  class="num-btn"
                  :class="{ active: token === page }"
                  :disabled="loading || token === page"
                  @click="goToPage(token)"
                >
                  {{ token }}
                </button>
                <span v-else class="ellipsis">...</span>
              </span>

              <button
                class="icon-btn"
                :disabled="page === totalPage || loading"
                @click="goToPage(page + 1)"
                aria-label="Next page"
                title="Next"
              >
                <Icon icon="mdi:chevron-right" width="18" height="18" />
              </button>

              <button
                class="icon-btn"
                :disabled="page === totalPage || loading"
                @click="goToPage(totalPage)"
                aria-label="Last page"
                title="Last"
              >
                <Icon icon="mdi:chevron-double-right" width="18" height="18" />
              </button>
            </div>
          </div>
          <!-- End Pagination -->
        </div>
      </div>

      <router-view />
    </transition>

    <!-- Filter Modal -->
    <div
      v-if="filterOpen"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filterTitle"
      id="filterModal"
      @keydown.esc="closeFilterModal"
      @click.self="closeOnBackdrop"
    >
      <div class="modal-panel" ref="panel">
        <div class="modal-header">
          <h3 id="filterTitle">Filter Users</h3>
          <button class="icon-btn sm" @click="closeFilterModal" aria-label="Close">
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        </div>

        <div class="modal-body">
          <div class="form-row">
            <label>Status</label>
            <select class="form-control" v-model="draftFilters.status">
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="banned">Banned</option>
            </select>
          </div>

          <div class="form-row">
            <label>Type</label>
            <select class="form-control" v-model="draftFilters.type">
              <option value="">All type</option>
              <option value="mahasiswa">Mahasiswa</option>
              <option value="dosen">Dosen</option>
              <option value="alumni">Alumni</option>
            </select>
          </div>

          <div class="form-row">
            <label>Faculty</label>
            <select class="form-control" v-model="draftFilters.faculity">
              <option value="">All faculty</option>
              <option v-for="f in faculties" :key="f" :value="f">{{ f }}</option>
            </select>
          </div>

          <div class="form-row">
            <label>Page size</label>
            <select class="form-control" v-model.number="draftFilters.perPage">
              <option :value="10">10 per page</option>
              <option :value="20">20 per page</option>
              <option :value="50">50 per page</option>
              <option :value="100">100 per page</option>
            </select>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-light" @click="resetDraft">Reset</button>
          <button class="btn btn-primary" @click="applyDraft" :disabled="loading">Apply</button>
        </div>
      </div>
    </div>

    <!-- Import Modal -->
    <div
      v-if="importOpen"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="importTitle"
      id="importModal"
      @keydown.esc="closeImportModal"
      @click.self="closeImportOnBackdrop"
    >
      <div class="modal-panel" ref="importPanel">
        <div class="modal-header">
          <h3 id="importTitle">Import Users from Excel</h3>
          <button class="icon-btn sm" @click="closeImportModal" aria-label="Close">
            <Icon icon="mdi:close" width="18" height="18" />
          </button>
        </div>

        <div class="modal-body">
          <p class="muted" style="margin:0 0 8px 0;">
            Download template lalu isi kolom berikut
            <b>username, email, firstname, lastname, password, generation, no_induk, type, faculity, phone</b>
          </p>
          <div class="form-row">
            <a :href="templateUrl" class="btn btn-light" target="_blank" rel="noopener">
              <Icon icon="mdi:file-download-outline" width="18" height="18" />
              Download template
            </a>
          </div>

          <div class="form-row">
            <label>File</label>
            <input type="file" class="form-control" accept=".xlsx,.csv" @change="onFileChange" />
          </div>

          <div v-if="importResult" class="import-result">
            <div class="result-line">Sukses: <b>{{ importResult.success }}</b></div>
            <div class="result-line">Gagal: <b>{{ importResult.failed }}</b></div>
            <div v-if="importResult.errors && importResult.errors.length" class="error-list">
              <div class="error-title">Detail error</div>
              <ul>
                <li v-for="(err, idx) in importResult.errors" :key="idx">{{ err }}</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-light" @click="resetImport" :disabled="uploading">Reset</button>
          <button class="btn btn-primary" @click="submitImport" :disabled="!importFile || uploading">
            <span v-if="!uploading">Upload</span>
            <span v-else>Uploading...</span>
          </button>
        </div>
      </div>
    </div>
    <!-- End Import Modal -->
  </div>
</template>

<script>
import axios from "axios";
import { Icon } from "@iconify/vue2";

function debounce(fn, delay = 400) {
  let t = null;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), delay);
  };
}

export default {
  name: "AllUser",
  components: { Icon },
  data() {
    return {
      all_user: [],
      search: "",
      totalPage: 1,
      page: 1,
      loading: false,
      windowSize: 5,

      filters: { status: "", type: "", faculity: "", perPage: 10 },
      filterOpen: false,
      draftFilters: { status: "", type: "", faculity: "", perPage: 10 },

      faculties: ["Engineering", "Business", "Medicine", "Law", "Pharmacy", "Psychology"],

      _debouncedSearch: null,

      importOpen: false,
      importFile: null,
      uploading: false,
      importResult: null,
      templateUrl: "/api/user/template"
    };
  },
  created() {
    this._debouncedSearch = debounce(this.searchChange.bind(this), 500);
    this.syncDraftFromActive();
    this.fetchData();
  },
  watch: {
    $route: "fetchData"
  },
  computed: {
    pageTokens() {
      const total = this.totalPage;
      const current = this.page;
      const win = this.windowSize;

      if (total <= win + 2) return Array.from({ length: total }, (_, i) => i + 1);

      const half = Math.floor(win / 2);
      let start = Math.max(2, current - half);
      let end = Math.min(total - 1, current + half);

      const needed = win - (end - start + 1);
      if (needed > 0) {
        if (start > 2) start = Math.max(2, start - needed);
        else if (end < total - 1) end = Math.min(total - 1, end + needed);
      }

      const tokens = [1];
      if (start > 2) tokens.push("...");
      for (let p = start; p <= end; p++) tokens.push(p);
      if (end < total - 1) tokens.push("...");
      tokens.push(total);
      return tokens;
    }
  },
  methods: {
    tokenKey(token) {
      return typeof token === "number" ? `p-${token}` : `e-${Math.random().toString(36).slice(2, 7)}`;
    },
    buildQuery() {
      const params = new URLSearchParams();
      params.set("search", this.search || "");
      params.set("page", String(this.page));
      if (this.filters.status) params.set("status", this.filters.status);
      if (this.filters.type) params.set("type", this.filters.type);
      if (this.filters.faculity) params.set("faculity", this.filters.faculity);
      if (this.filters.perPage) params.set("per_page", String(this.filters.perPage));
      return params.toString();
    },
    async fetchData() {
      if (this.$route.name !== "AllUser") return;
      this.loading = true;
      try {
        const query = this.buildQuery();
        const res = await axios.get(`user/all?${query}`);
        this.all_user = res?.data?.data || [];
        this.page = Number(res?.data?.current_page) || 1;
        this.totalPage = Number(res?.data?.last_page) || 1;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPage || p === this.page) return;
      this.page = p;
      this.fetchData();
    },
    onSearchInput() {
      this._debouncedSearch();
    },
    searchChange() {
      this.page = 1;
      this.fetchData();
    },
    // Filter modal
    openFilterModal() {
      this.syncDraftFromActive();
      this.filterOpen = true;
      document.body.style.overflow = "hidden";
    },
    closeFilterModal() {
      this.filterOpen = false;
      document.body.style.overflow = "";
    },
    closeOnBackdrop(e) {
      if (e.target === e.currentTarget) this.closeFilterModal();
    },
    syncDraftFromActive() {
      this.draftFilters = { ...this.filters };
    },
    resetDraft() {
      this.draftFilters = { status: "", type: "", faculity: "", perPage: 10 };
    },
    applyDraft() {
      this.filters = { ...this.draftFilters };
      this.page = 1;
      this.closeFilterModal();
      this.fetchData();
    },
    // Import modal
    openImportModal() {
      this.importOpen = true;
      this.importResult = null;
      this.importFile = null;
      document.body.style.overflow = "hidden";
    },
    closeImportModal() {
      this.importOpen = false;
      document.body.style.overflow = "";
    },
    closeImportOnBackdrop(e) {
      if (e.target === e.currentTarget) this.closeImportModal();
    },
    onFileChange(e) {
      this.importFile = e.target.files?.[0] || null;
    },
    resetImport() {
      this.importFile = null;
      this.importResult = null;
    },
    async submitImport() {
      if (!this.importFile) return;
      this.uploading = true;
      try {
        const form = new FormData();
        form.append("file", this.importFile);
        const res = await axios.post("user/import", form, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        this.importResult = res?.data || { success: 0, failed: 0, errors: [] };
        this.page = 1;
        await this.fetchData();
      } catch (e) {
        console.error(e);
        this.importResult = { success: 0, failed: 0, errors: ["Gagal mengunggah atau memproses file"] };
      } finally {
        this.uploading = false;
      }
    },
    // Actions
    banned(id, type) {
      const parents = this;
      this.$swal({
        title: "Are you sure?",
        text: "",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, " + type + " it!"
      }).then(result => {
        if (result.isConfirmed) {
          axios
            .get("user/banned/" + id)
            .then(function (response) {
              if (response.data.status) {
                parents.$swal({ icon: "success", title: "Success", text: response.data.message })
                  .then(() => { parents.searchChange(); });
              } else {
                parents.$swal({ icon: "error", title: "Oops...", text: "Something Wrong" });
              }
            })
            .catch(function () {
              parents.$swal({ icon: "error", title: "Oops...", text: "Something Wrong" });
            });
        }
      });
    }
  }
};
</script>

<style scoped>
/* Header */
.header-bar { display:flex; align-items:center; gap:12px; justify-content:space-between; margin-bottom:12px; }
.header-bar h2 { margin:0; font-size:18px; font-weight:600; }
.actions { display:flex; align-items:center; gap:10px; }

/* Search */
.search { position:relative; min-width:260px; width:320px; }
.search-icon { position:absolute; top:10px; left:12px; color:#9ca3af; }
.search-input { width:100%; height:38px; border:1px solid #e5e7eb; border-radius:10px; padding:0 12px 0 36px; font-size:14px; background:#fff; transition:border-color .15s, box-shadow .15s; }
.search-input:focus { outline:none; border-color:#c7d2fe; box-shadow:0 0 0 3px rgba(59,130,246,.15); }

/* Buttons */
.btn { height:38px; padding:0 12px; border-radius:10px; border:1px solid transparent; display:inline-flex; gap:8px; align-items:center; justify-content:center; font-size:14px; transition:background-color .15s, border-color .15s, color .15s, transform .05s; }
.btn:active { transform:translateY(1px); }
.btn-primary { background:#2563eb; color:#fff; border-color:#2563eb; }
.btn-primary:hover { background:#1d4ed8; border-color:#1d4ed8; }
.btn-light { background:#fff; color:#374151; border-color:#e5e7eb; }
.btn-light:hover { background:#f9fafb; }
.btn-outline { background:#fff; color:#1f2937; border-color:#e5e7eb; }
.btn-outline:hover { background:#f9fafb; }

/* Card */
.card { background:#fff; border:1px solid #eef2f7; border-radius:14px; padding:16px; box-shadow:0 1px 2px rgba(16,24,40,.04); }

/* Table */
.table-wrapper { overflow-x:auto; }
.table { width:100%; border-collapse:separate; border-spacing:0; }
.table thead th { text-align:left; font-weight:600; font-size:13px; color:#6b7280; padding:10px 12px; border-bottom:1px solid #eef2f7; background:#fafafa; }
.table tbody td { padding:12px; border-bottom:1px solid #f3f4f6; font-size:14px; vertical-align:middle; }
.table tbody tr:hover td { background:#fafafa; }
.w-150 { width:150px; }
.muted { color:#6b7280; }

/* Row actions */
.row-actions { display:inline-flex; gap:8px; }
.btn-danger { background:#fee2e2; border-color:#fecaca; color:#b91c1c; }
.btn-danger:hover { background:#fecaca; }
.btn-warning { background:#fff7ed; border-color:#fed7aa; color:#c2410c; }
.btn-warning:hover { background:#ffedd5; }

/* Badges */
.badge { display:inline-flex; align-items:center; height:24px; border-radius:9999px; padding:0 10px; font-size:12px; border:1px solid transparent; }
.badge-success { background:#ecfdf5; border-color:#a7f3d0; color:#065f46; }
.badge-danger { background:#fef2f2; border-color:#fecaca; color:#991b1b; }

/* Empty state */
.empty-state { text-align:center; color:#9ca3af; height:80px; }
.empty-state span { margin-left:6px; }

/* Loader */
.loading-wrap { display:flex; justify-content:center; padding:10px 0; }

/* Pagination */
.pager-wrap { display:flex; align-items:center; justify-content:space-between; margin-top:12px; padding-top:10px; border-top:1px solid #eef2f7; }
.pager-info { font-size:13px; color:#6b7280; }
.pager-controls { display:inline-flex; align-items:center; gap:8px; }

/* Icon buttons */
.icon-btn { display:inline-flex; align-items:center; justify-content:center; height:36px; width:36px; border-radius:10px; background:#fff; border:1px solid #e5e7eb; box-shadow:0 1px 2px rgba(16,24,40,.04); transition:background-color .15s, border-color .15s, opacity .15s, transform .05s; }
.icon-btn:hover { background:#f9fafb; }
.icon-btn:active { transform:translateY(1px); }
.icon-btn:disabled { opacity:.5; cursor:not-allowed; }
.icon-btn.sm { height:30px; width:30px; border-radius:8px; }

/* Number buttons */
.num-btn { min-width:36px; height:36px; padding:0 10px; border-radius:10px; background:#fff; border:1px solid #e5e7eb; font-size:13px; color:#374151; transition:background-color .15s, color .15s, border-color .15s, transform .05s; }
.num-btn:hover { background:#f9fafb; }
.num-btn:active { transform:translateY(1px); }
.num-btn.active { background:#2563eb; border-color:#2563eb; color:#fff; cursor:default; }

/* Ellipsis */
.ellipsis { display:inline-flex; align-items:center; justify-content:center; width:24px; font-size:16px; color:#9ca3af; user-select:none; }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,.25); display:flex; align-items:flex-start; justify-content:center; padding:40px 16px; z-index:999; }
.modal-panel { width:100%; max-width:520px; background:#fff; border-radius:14px; border:1px solid #eef2f7; box-shadow:0 16px 40px rgba(2,6,23,.15); overflow:hidden; animation:modalIn .15s ease-out; }
@keyframes modalIn { from{opacity:0; transform:translateY(8px);} to{opacity:1; transform:translateY(0);} }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:14px 16px; border-bottom:1px solid #eef2f7; }
.modal-header h3 { margin:0; font-size:16px; font-weight:600; }
.modal-body { padding:14px 16px 4px 16px; }
.form-row { display:flex; flex-direction:column; gap:6px; margin-bottom:12px; }
.form-row label { font-size:13px; color:#6b7280; }
.form-control { height:38px; border:1px solid #e5e7eb; background:#fff; border-radius:10px; padding:0 10px; font-size:14px; }
.form-control:focus { outline:none; border-color:#c7d2fe; box-shadow:0 0 0 3px rgba(59,130,246,.15); }
.modal-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; padding:12px 16px; border-top:1px solid #eef2f7; }

/* Import result */
.import-result { margin-top:8px; padding:10px; border:1px solid #e5e7eb; border-radius:10px; background:#fafafa; }
.import-result .result-line { font-size:13px; margin-bottom:4px; color:#374151; }
.import-result .error-title { margin-top:8px; font-weight:600; }
.import-result ul { margin:4px 0 0 18px; }

/* Responsive */
@media (max-width:768px){
  .actions{ width:100%; }
  .search{ width:100%; min-width:0; }
  .header-bar{ flex-direction:column; align-items:stretch; gap:8px; }
  .pager-wrap{ flex-direction:column; gap:8px; align-items:flex-start; }
}
</style>